import React, { useCallback, useId, useMemo } from "react"

import { useMetricsListRecordDataTransport } from "@/metrics/transport-data"
import {
  Card,
  CardContent,
  GroupLabel,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
} from "@coval/ui"
import { X } from "lucide-react"

import type { MetricRecordData } from "@/types/metrics/contracts"

import { RequiredMarker } from "../../configuration/components/RequiredMarker"
import type { AggregationType } from "../../hooks/useEligibleParentMetrics"
import { EligibleParentsPicker } from "./EligibleParentsPicker"
import { VALIDATION_MESSAGES } from "./validationMessages"

export type { AggregationType }

export interface AggregateMetadata {
  derived_type: "aggregate"
  aggregation_type: AggregationType
  parent_metric_ids: string[]
  count_where_target?: string
}

interface Props {
  metadata: AggregateMetadata
  onChange: (next: AggregateMetadata) => void
  currentMetricId: string | null
}

const AGGREGATION_TYPE_OPTIONS: {
  value: AggregationType
  label: string
  description: string
}[] = [
  { value: "AVERAGE", label: "AVERAGE", description: "Mean of parent values" },
  { value: "SUM", label: "SUM", description: "Sum of parent values" },
  { value: "MIN", label: "MIN", description: "Minimum parent value" },
  { value: "MAX", label: "MAX", description: "Maximum parent value" },
  {
    value: "COUNT",
    label: "COUNT",
    description: "Count of non-null parent values",
  },
  {
    value: "COUNT_WHERE",
    label: "COUNT_WHERE",
    description: "Count of parents matching a target value",
  },
]

function inferAggregateUnit(
  parentMetrics: MetricRecordData[],
  aggregationType: AggregationType,
): { label: string; isError: boolean } {
  if (aggregationType === "COUNT" || aggregationType === "COUNT_WHERE") {
    return { label: "count", isError: false }
  }

  const units = parentMetrics.map((m) => m.metric_metadata?.units ?? null)

  if (units.every((u) => u === null)) {
    return { label: "(unitless)", isError: false }
  }

  const definedUnits = units.filter((u): u is string => u !== null)
  const unique = Array.from(new Set(definedUnits))

  if (unique.length === 1) {
    return { label: unique[0], isError: false }
  }

  return {
    label: `(invalid: mixed units — ${unique.join(", ")})`,
    isError: true,
  }
}

export function validateAggregateMetadata(
  metadata: AggregateMetadata,
): string | null {
  const parentMetricIds = metadata?.parent_metric_ids ?? []
  if (parentMetricIds.length === 0) {
    return VALIDATION_MESSAGES.AGGREGATE_NO_PARENTS
  }
  if (
    metadata.aggregation_type === "COUNT_WHERE" &&
    !metadata.count_where_target?.trim()
  ) {
    return VALIDATION_MESSAGES.AGGREGATE_COUNT_WHERE_NO_TARGET
  }
  return null
}

export const DerivedAggregateConfig: React.FC<Props> = ({
  metadata,
  onChange,
  currentMetricId,
}) => {
  const baseId = useId()
  const aggregationTypeId = `${baseId}-aggregation-type`
  const parentMetricsLabelId = `${baseId}-parent-metrics`
  const { data: metricsData } = useMetricsListRecordDataTransport()
  const allMetrics: MetricRecordData[] = useMemo(
    () => Object.values(metricsData ?? {}),
    [metricsData],
  )

  // Tolerate metadata persisted before the parent_metric_ids field existed.
  const parentMetricIds = useMemo(
    () => metadata.parent_metric_ids ?? [],
    [metadata.parent_metric_ids],
  )

  const selectedMetrics = useMemo(
    () => allMetrics.filter((m) => m.id && parentMetricIds.includes(m.id)),
    [allMetrics, parentMetricIds],
  )

  const unitPreview = useMemo(
    () => inferAggregateUnit(selectedMetrics, metadata.aggregation_type),
    [selectedMetrics, metadata.aggregation_type],
  )

  const handleAggregationTypeChange = useCallback(
    (value: string) => {
      const next: AggregateMetadata = {
        ...metadata,
        aggregation_type: value as AggregationType,
        parent_metric_ids: [],
      }
      if (value !== "COUNT_WHERE") {
        delete next.count_where_target
      }
      onChange(next)
    },
    [metadata, onChange],
  )

  const handleCountWhereTargetChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...metadata, count_where_target: e.target.value })
    },
    [metadata, onChange],
  )

  const handleParentIdsChange = useCallback(
    (next: string[]) => {
      onChange({ ...metadata, parent_metric_ids: next })
    },
    [metadata, onChange],
  )

  const handleRemoveParent = useCallback(
    (metricId: string) => {
      onChange({
        ...metadata,
        parent_metric_ids: parentMetricIds.filter((id) => id !== metricId),
      })
    },
    [metadata, parentMetricIds, onChange],
  )

  const validationError = useMemo(
    () => validateAggregateMetadata(metadata),
    [metadata],
  )

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-4 space-y-3">
          <div className="space-y-2">
            <Label
              htmlFor={aggregationTypeId}
              id={`${aggregationTypeId}-label`}
            >
              Aggregation type
            </Label>
            <Select
              value={metadata.aggregation_type}
              onValueChange={handleAggregationTypeChange}
            >
              <SelectTrigger
                id={aggregationTypeId}
                aria-labelledby={`${aggregationTypeId}-label`}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AGGREGATION_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    <span className="font-medium">{opt.label}</span>
                    <span className="ml-2 text-xs text-coval-text-muted">
                      — {opt.description}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {metadata.aggregation_type === "COUNT_WHERE" && (
            <div className="space-y-2">
              <Label htmlFor="count-where-target">
                Target value
                <RequiredMarker />
              </Label>
              <Input
                id="count-where-target"
                type="text"
                value={metadata.count_where_target ?? ""}
                onChange={handleCountWhereTargetChange}
                placeholder="e.g. YES"
                aria-label="COUNT_WHERE target value"
              />
              <p className="text-xs text-coval-text-muted">
                Count parents whose string output equals this value.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent
          role="group"
          aria-labelledby={parentMetricsLabelId}
          className="pt-4 space-y-3"
        >
          <GroupLabel id={parentMetricsLabelId}>
            Parent metrics
            <RequiredMarker />
          </GroupLabel>

          {selectedMetrics.length > 0 && (
            <div className="space-y-1">
              {selectedMetrics.map((m) => (
                <div
                  key={m.id}
                  className="flex items-center gap-2 px-3 py-2 border border-coval-border rounded-lg bg-surface-default"
                >
                  <span className="flex-1 text-sm font-medium text-coval-text truncate">
                    {m.metric_display_name || m.metric_name}
                  </span>
                  <Tooltip
                    content={`Remove parent metric ${m.metric_display_name || m.metric_name}`}
                  >
                    <button
                      type="button"
                      onClick={() => handleRemoveParent(m.id ?? "")}
                      aria-label={`Remove parent metric ${m.metric_display_name || m.metric_name}`}
                      className="text-coval-text-muted hover:text-coval-danger-text flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </Tooltip>
                </div>
              ))}
            </div>
          )}

          {selectedMetrics.length === 0 && (
            <p className="text-sm text-coval-text-muted">
              No parent metrics selected yet.
            </p>
          )}

          <EligibleParentsPicker
            metrics={allMetrics}
            targetMetricType="aggregate"
            aggregationType={metadata.aggregation_type}
            selectedParentIds={parentMetricIds}
            selfId={currentMetricId ?? undefined}
            onChange={handleParentIdsChange}
          />

          {selectedMetrics.length > 0 && (
            <div className="pt-1">
              <span className="text-xs text-coval-text-muted">
                Result unit:{" "}
              </span>
              <span
                className={`text-xs font-medium ${unitPreview.isError ? "text-coval-danger-text" : "text-coval-text-muted"}`}
              >
                {unitPreview.label}
              </span>
            </div>
          )}

          {validationError && (
            <p className="text-sm text-coval-danger-text" role="alert">
              {validationError}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
